import React from "react";
import { TextField, Box } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import axios from "axios";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_AUTOCOMPLETE;

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

export default function GoogleMaps(props) {
  const [value, setValue] = React.useState(props.defaultValue ? props.defaultValue : null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const loaded = React.useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector("head"),
        "google-maps",
      );
    }

    loaded.current = true;
  }

  const fetch = React.useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    [],
  );

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue,types: ["address"], }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  async function handleValueSelect(value) {
    let strAddr = "";
    for(let i=0;i<value?.terms.length-3; ++i){
      if(strAddr==="")
        strAddr = value.terms[i].value;
      else
        strAddr = strAddr+", "+value.terms[i].value;
    }
    setValue(strAddr);
    props.handlePlaceSelect();
    var config = {
      method: "get",
      url: `https://maps.googleapis.com/maps/api/geocode/json?place_id=${value?.place_id}&key=${GOOGLE_MAPS_API_KEY}`,
      headers: {},
    };

    axios(config)
      .then(function (response) {
        let arr = response?.data?.results?.[0]?.address_components;
        let position = arr.findIndex(
          (element) => element.types && element.types[0] == "postal_code"
        );
        if (position !== -1)
          value.zip =
            response?.data?.results?.[0]?.address_components?.[position]?.[
              "long_name"
            ];
        value.location = response?.data?.results?.[0]?.geometry?.location;
        props.handlePlaceSelect(value);
      })
      .catch(function (error) {

      });
  }

  return (
    <Autocomplete
      className="loc-autocomplete"
      id="google-map-demo"
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        handleValueSelect(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" fullWidth />
      )}
      renderOption={(props, option) => {
        let parts;
        if( typeof option === "object")
          {
            const matches = option?.structured_formatting?.main_text_matched_substrings;
        parts = parse(
          option?.structured_formatting?.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );
          }
        else 
          parts = [];
        

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item>
                <Box
                  component={LocationOnIcon}
                  sx={{ color: "text.secondary", mr: 2 }}
                />
              </Grid>
              <Grid item xs>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                  >
                    {part.text}
                  </span>
                ))}

                <Typography variant="body2" color="text.secondary">
                  {option?.structured_formatting?.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
